import { ThemeProvider, Box, VStack, Text } from "@chakra-ui/react";
import { extendTheme } from "@chakra-ui/react";
import { useState, useEffect, memo, useMemo, useCallback } from "react";
import { OrderStatus } from "../enums";
import { OrderHandlers } from "../index";
import { useGetAllSuppliers, useGetBrandsInfo } from "../queryHooks";
import { useGetChildOrders } from "../queryHooks";
import { useOrdersStore } from "../store/useOrdersStore";
import GaraazOrderCard from "./GaraazOrderCard";

const theme = extendTheme({
  components: {
    Text: {
      baseStyle: {
        fontSize: "small",
      },
    },
  },
});

export type OrderRemark = {
  userName: string;
  remarks: string;
};

export type AllocatedTo = {
  supplierId: string;
  branchId: string;
  orderRemarks: string;
  orderStatus: string;
  invoiceInfo?: any;
  isActive: boolean;
  customerType?: string;
  discount?: number;
  parentSupplierId?: string;
};

export const finalOrcaStates = [
  OrderStatus.CANCELLED,
  OrderStatus.CUSTOMER_RETURN_REJECTED,
  OrderStatus.SUPPLIER_RETURNED,
  OrderStatus.SUPPLIER_RETURN_REJECTED,
  OrderStatus.DELIVERED,
  OrderStatus.ACCEPTED,
  OrderStatus.REJECTED,
];

export const startOrcaStates = [
  OrderStatus.NEW,
  OrderStatus.RETURN_REQUESTED,
];

type Props = {
  orderData: any;
  orderHandlers: OrderHandlers;
  orderCancellationReason: string[];
  refetchAllOrders: () => void;
  filterString: string;
};

const useChildOrders = (orderId: string) => {
  return useOrdersStore(
    useCallback(
      (state) => {
        if (!orderId) return [];
        if (state.childOrders && state.childOrders.has(orderId)) {
          return state.childOrders.get(orderId) || []
        } else {
          return [];
        }
      },
      [orderId]
    )
  );
};

interface childOrderSectionProps {
  childOrders: any[];
  isLoadingChildOrders: boolean;
  getChildOrderCardProps: (childOrder: any) => any;
  descendantOrderIdsLength: number;
}

const ChildOrdersSection = memo(({
  childOrders,
  isLoadingChildOrders,
  getChildOrderCardProps,
  descendantOrderIdsLength,
}: childOrderSectionProps) => {
  const remainingChildren = descendantOrderIdsLength - childOrders?.length;

  if (isLoadingChildOrders) {
    return (
      <Box pl="40px" mt={4}>
        <Text color="gray.500" fontSize="sm">Loading child orders...</Text>
      </Box>
    );
  }

  if (!childOrders || childOrders.length === 0) {
    return (
      <Box pl="40px" mt={4}>
        <Text color="gray.500" fontSize="sm">No child orders found</Text>
      </Box>
    );
  }

  return (
    <VStack
      spacing={0}
      align="stretch"
      pl="40px"
      position="relative"
      width="65vw"
      maxHeight={"3000px"}
    >
      {childOrders.map((childOrder: any, index: number) => {
        const childOrderHeight = `calc(100% - ${remainingChildren > 0 ? 0 : childOrders.length == index + 1 ? 50 : 0}%)`
        return (
          <>
            <Box
              key={childOrder._id}
              position="relative"
            >
              <Box
                position="absolute"
                left="-8px"
                top="0"
                bottom="0"
                width="4px"
                height={childOrderHeight}
                bg="gray.200"
              />
              <Box
                position="absolute"
                left="-8px"
                top="50%"
                width="20px"
                height="4px"
                bg="gray.200"
              />
              <Box mt="20px" position="relative">
                <GaraazOrderCard {...getChildOrderCardProps(childOrder)} />
              </Box>
            </Box>
          </>
        );
      })}
      {
        remainingChildren > 0 && (
          <Box
            key={`${childOrders[0]._id}-${childOrders.length}-${remainingChildren}`}
            position="relative"
          >
            <Box
              position="absolute"
              left="-8px"
              top="-175%"
              bottom="0"
              width="4px"
              height={`250%`}
              borderLeft="4px dashed"
              borderColor="gray.300"
              zIndex={0}
            />
            <Box
              left="-8px"
              top="75%"
              width="20px"
              height="4px"
              position="absolute"
              borderTop="4px dashed"
              borderColor="gray.300"
              zIndex={0}
            />

            <Box
              width="calc(65vw - 55px)"
              position="relative"
              mt="20px"
              display="flex"
              height={"100%"}
              alignItems="center"
              ml="15px"
              border="2px dashed"
              borderColor="gray.300"
              borderRadius="4px"
              p={2}
              bg="gray.50"
              justifyContent="center"
              flex="1"
              zIndex={1}
              _hover={{
                borderColor: "gray.400",
                bg: "gray.100",
                transition: "all 0.3s ease-in-out",
                boxShadow: "0 0 10px 0 rgba(0, 0, 0, 0.1)",
              }}
            >
              <Text fontSize="sm" color="gray.600" fontWeight="medium">
                +{remainingChildren} more Child Orders
              </Text>
              <Text fontSize="sm" color="gray.500" ml={2}>
                ( filtered out )
              </Text>
            </Box>
          </Box>
        )
      }
    </VStack>
  );
});

ChildOrdersSection.displayName = 'ChildOrdersSection';

const GaraazOrderComponent: React.FC<Props> = memo((props: Props) => {
  const { orderData, orderHandlers, refetchAllOrders, filterString } = props;
  const { data: allSuppliers } = useGetAllSuppliers();
  const { data: allBrands } = useGetBrandsInfo();

  const [isCustomerOrderFreezed, setIsCustomerOrderFreezed] = useState(false);
  const [isChildOrdersFrozen, setIsChildOrdersFrozen] = useState(false);
  const [isPartsFreezed, setIsPartsFreezed] = useState(true);
  const childOrders = useChildOrders(orderData._id);
  const [isChildOrdersVisible, setIsChildOrdersVisible] = useState(false);
  const { refetch: reFetchChildOrders, isLoading: isLoadingChildOrders } = useGetChildOrders(
    orderData.descendantOrderIds?.length ? orderData._id : '',
    filterString
  );

  const handleToggleChildOrders = useCallback((orderId: string) => {
    if (orderId === orderData._id && orderData.descendantOrderIds?.length) {
      if (!isChildOrdersVisible) {
        setIsChildOrdersVisible(true);
        reFetchChildOrders();
      } else {
        setIsChildOrdersVisible(false);
      }
    }
  }, [orderData._id, orderData.descendantOrderIds, isChildOrdersVisible, reFetchChildOrders]);

  useEffect(() => {
    const orderDate = new Date(orderData.orderDate);
    const isCustomerBlocked = orderData.customer.isBlocked;

    if (isCustomerBlocked) {
      setIsChildOrdersFrozen(true);
      setIsCustomerOrderFreezed(true);
    }
    else {
      setIsChildOrdersFrozen(false);
      setIsCustomerOrderFreezed(false);
    }
  }, [orderData.customer.isBlocked, orderData.orderDate, orderData.descendantOrderIds, childOrders?.length]);

  const getSupplierBusinessName = useCallback(
    (supplierId: string) => {
      const supplier = allSuppliers?.find(
        (supplier: any) => supplier._id === supplierId
      );
      return supplier ? supplier.businessName : "";
    },
    [allSuppliers]
  );

  const getSupplierParentId = useCallback(
    (supplierId: string) => {
      const supplier = allSuppliers?.find(
        (supplier: any) => supplier._id === supplierId
      );
      return supplier ? supplier.parentSupplierId : "";
    },
    [allSuppliers]
  );

  const getSupplierBranchName = useCallback(
    (supplierId: any, branchId: any) => {
      const supplier = allSuppliers?.find(
        (supplier: any) => supplier._id === supplierId
      );
      const branch = supplier?.branches?.find(
        (branch: any) => branch._id === branchId
      );
      return branch ? branch.branchName : "";
    },
    [allSuppliers]
  );

  const getSupplierNumber = useCallback(
    (supplierId: string, branchId?: string | undefined) => {
      const supplier = allSuppliers?.find(
        (supplier: { _id: string }) => supplier._id === supplierId
      );
      const branch = supplier?.branches?.find(
        (branch: { _id: string }) => branch._id === branchId
      );
      return branch
        ? branch.branchPrimaryMobileNumber
        : supplier
          ? supplier.user?.mobileNumber
          : "";
    },
    [allSuppliers]
  );

  const findBrandName = useCallback(
    (brandId: string) => {
      const brand = allBrands?.find((brand: any) => brand._id === brandId);
      return brand ? brand.name.split(" ")[0] : "";
    },
    [allBrands]
  );

  const parentOrderCardProps = useMemo(() => ({
    orderData,
    orderHandlers,
    orderCancellationReason: props.orderCancellationReason,
    refetchAllOrders,
    isChildOrder: false,
    onToggleChildOrders: handleToggleChildOrders,
    reFetchChildOrders,
    isChildOrdersVisible,
    findBrandName,
    getSupplierBusinessName,
    getSupplierParentId,
    getSupplierBranchName,
    getSupplierNumber,
    isCustomerOrderFreezed,
  }), [
    orderData,
    orderHandlers,
    props.orderCancellationReason,
    refetchAllOrders,
    handleToggleChildOrders,
    reFetchChildOrders,
    isChildOrdersVisible,
    findBrandName,
    getSupplierBusinessName,
    getSupplierParentId,
    getSupplierBranchName,
    getSupplierNumber,
    isCustomerOrderFreezed,
  ]);

  const getChildOrderCardProps = useCallback((childOrder: any) => {
    return {
      orderData: childOrder,
      orderHandlers,
      orderCancellationReason: props.orderCancellationReason,
      refetchAllOrders: reFetchChildOrders,
      isChildOrder: true,
      findBrandName,
      getSupplierBusinessName,
      getSupplierParentId,
      getSupplierBranchName,
      getSupplierNumber,
      isCustomerOrderFreezed,
      isPartsFreezed,
    };
  }, [
    orderHandlers,
    props.orderCancellationReason,
    reFetchChildOrders,
    findBrandName,
    getSupplierBusinessName,
    getSupplierParentId,
    getSupplierBranchName,
    getSupplierNumber,
    isCustomerOrderFreezed,
    isPartsFreezed,
  ]);

  return (
    <ThemeProvider theme={theme}>
      <VStack spacing={0} align="stretch">
        <GaraazOrderCard {...parentOrderCardProps} />

        {orderData.descendantOrderIds?.length > 0 && isChildOrdersVisible && (
          <ChildOrdersSection
            childOrders={childOrders || []}
            isLoadingChildOrders={isLoadingChildOrders}
            getChildOrderCardProps={getChildOrderCardProps}
            descendantOrderIdsLength={orderData.descendantOrderIds?.length}      
          />
        )}
      </VStack>
    </ThemeProvider>
  );
});

GaraazOrderComponent.displayName = 'GaraazOrderComponent';

export default GaraazOrderComponent;
